/**
 * Utility functions for formatting data
 */

/**
 * Format a number as currency
 * @param value - The number to format
 * @param currency - The currency code (optional, will use system default if not provided)
 * @param locale - The locale to use for formatting (default: en-US)
 * @returns Formatted currency string
 */
export const formatCurrency = (
    value: number,
    currency?: string,
    locale: string = 'en-US'
): string => {
    // Use provided currency or fallback to USD if system config isn't available
    const currencyCode = currency || 'USD';

    try {
        // Try to format as currency first (for valid ISO 4217 codes)
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    } catch (error) {
        // If currency code is invalid (like crypto symbols), format as number with symbol
        return (
            new Intl.NumberFormat(locale, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(value) + ` ${currencyCode}`
        );
    }
};

/**
 * Format currency with system default currency
 * This function should be used with a currency context or system config
 * @param value - The number to format
 * @param systemCurrency - The system default currency (from config)
 * @param locale - The locale to use for formatting
 */
export const formatCurrencyWithDefault = (
    value: number,
    systemCurrency: string = 'USD',
    locale: string = 'en-US'
): string => {
    return formatCurrency(value, systemCurrency, locale);
};

/**
 * Format a percentage
 * @param value - The number to format as percentage
 * @param locale - The locale to use for formatting (default: en-US)
 * @returns Formatted percentage string
 */
export const formatPercentage = (value: number, locale: string = 'en-US'): string => {
    return new Intl.NumberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value / 100);
};

/**
 * Format a number with specified decimal places
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @param locale - The locale to use for formatting (default: en-US)
 * @returns Formatted number string
 */
export const formatNumber = (
    value: number,
    decimals: number = 2,
    locale: string = 'en-US'
): string => {
    // Ensure decimals is within valid range for Intl.NumberFormat (0-7)
    const validDecimals = Math.max(0, Math.min(7, Math.round(decimals)));

    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: validDecimals,
        maximumFractionDigits: validDecimals,
    }).format(value);
};

/**
 * Format a date with time (dd.MM.yyyy HH:mm format).
 * When timeZone is provided (e.g. tenant timezone in CRM), the date is shown in that zone.
 * API always returns datetimes in UTC (ISO 8601 with Z); pass timeZone in CRM to display in tenant timezone.
 * @param date - The date to format (UTC ISO string or Date)
 * @param locale - The locale to use for formatting (default: en-GB for dd.MM.yyyy)
 * @param timeZone - IANA timezone (e.g. "Asia/Jerusalem") for CRM; omit for trader/browser local
 */
export const formatDate = (
    date: string | Date | null | undefined,
    locale: string = 'en-GB',
    timeZone?: string
): string => {
    if (!date) {
        return '--';
    }

    const options: Intl.DateTimeFormatOptions = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false, // 24h format
    };
    if (timeZone) {
        options.timeZone = timeZone;
    }
    return new Date(date).toLocaleString(locale, options);
};

/**
 * Format a date only (dd.MM.yyyy format).
 * @param timeZone - IANA timezone for CRM; omit for browser local
 */
export const formatDateOnly = (
    date: string | Date,
    locale: string = 'en-GB',
    timeZone?: string
): string => {
    const options: Intl.DateTimeFormatOptions = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    };
    if (timeZone) {
        options.timeZone = timeZone;
    }
    return new Date(date).toLocaleDateString(locale, options);
};

/**
 * Format time only (HH:mm format)
 * @param date - The date to format
 * @param locale - The locale to use for formatting (default: en-GB)
 * @returns Formatted time string
 */
export const formatTimeOnly = (date: string | Date, locale: string = 'en-GB'): string => {
    return new Date(date).toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false, // 24h format
    });
};

/**
 * Convert a UTC ISO datetime (from API) to "yyyy-MM-ddTHH:mm" for use in <input type="datetime-local" />
 * in a given timezone. Use in CRM when initialising lead/customer datetime fields.
 */
export const utcIsoToDatetimeLocal = (
    utcIso: string | null | undefined,
    timeZone: string
): string => {
    if (!utcIso) return '';
    const d = new Date(utcIso);
    if (isNaN(d.getTime())) return '';
    // sv-SE gives yyyy-MM-dd; with hour/minute we get "2025-03-03 17:30"
    const s = d.toLocaleString('sv-SE', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
    return s.replace(' ', 'T');
};

/**
 * Resolve decimal precision for an instrument from its config (quote_precision).
 * Falls back to symbol-based heuristics when config is unavailable.
 */
export const getInstrumentQuotePrecision = (
    instrument?: { quote_precision?: number; symbol?: string } | null,
    symbol?: string,
    fallbackDecimals: number = 5
): number => {
    if (instrument?.quote_precision !== undefined && instrument.quote_precision !== null) {
        const precision = Number(instrument.quote_precision);
        if (!isNaN(precision) && precision >= 0 && precision <= 18) {
            return Math.round(precision);
        }
    }

    const sym = (instrument?.symbol || symbol || '').toUpperCase();
    if (sym.includes('JPY')) {
        return 3;
    }
    if (sym.includes('BTC') || sym.includes('ETH')) {
        return 2;
    }
    if (sym.includes('USD') || sym.includes('EUR') || sym.includes('GBP')) {
        return 5;
    }

    const safeFallback = Number(fallbackDecimals);
    return !isNaN(safeFallback) && safeFallback >= 0 && safeFallback <= 18
        ? Math.round(safeFallback)
        : 5;
};

/**
 * Format instrument price based on instrument's quote_precision
 * Shows up to 5 decimals (or instrument precision) and removes trailing zeros dynamically
 * @param price - The price to format
 * @param instrument - The instrument object with quote_precision property
 * @param fallbackDecimals - Fallback decimal places if instrument precision is not available (default: 5)
 * @param locale - The locale to use for formatting (default: en-US)
 * @returns Formatted price string with trailing zeros removed
 */
export const formatInstrumentPrice = (
    price: number,
    instrument?: { quote_precision?: number; symbol?: string } | null,
    fallbackDecimals: number = 5,
    locale: string = 'en-US'
): string => {
    // Validate fallbackDecimals parameter to ensure it's a valid number
    const safeFallback = Number(fallbackDecimals);
    const validFallback =
        !isNaN(safeFallback) && safeFallback >= 0 && safeFallback <= 7
            ? Math.round(safeFallback)
            : 5; // Default to 5 if fallback is invalid

    let maxDecimals = Math.min(
        getInstrumentQuotePrecision(instrument, undefined, validFallback),
        5
    );

    // Final safety check: ensure maxDecimals is always a valid number within range (2-7)
    // This prevents any edge cases where maxDecimals could be NaN, undefined, or out of range
    const finalMaxDecimals = Number(maxDecimals);
    if (isNaN(finalMaxDecimals) || finalMaxDecimals < 2 || finalMaxDecimals > 7) {
        maxDecimals = 5; // Safe default
    } else {
        maxDecimals = Math.max(2, Math.min(7, Math.round(finalMaxDecimals)));
    }

    // Format with minimum 2 decimal places, but up to maxDecimals
    // This ensures 0 shows as "0.00" but 1.23000 shows as "1.23"
    const formatted = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: maxDecimals,
    }).format(price);

    // Remove trailing zeros beyond 2 decimal places
    // This handles cases like 1.230000 -> 1.23 but keeps 0.00 as "0.00"
    // Split by decimal point
    const parts = formatted.split('.');
    if (parts.length === 2) {
        // Remove trailing zeros but keep at least 2 digits
        const decimals = parts[1].replace(/0+$/, '');
        if (decimals.length >= 2) {
            return `${parts[0]}.${decimals}`;
        } else {
            // Pad to 2 digits if needed
            return `${parts[0]}.${decimals.padEnd(2, '0')}`;
        }
    }

    // If no decimal point, add .00 for whole numbers
    return `${formatted}.00`;
};

/**
 * Format account balances, transaction amounts, and prices
 * Shows up to 5 decimals and removes trailing zeros dynamically
 * @param value - The value to format
 * @param maxDecimals - Maximum decimal places (default: 5)
 * @param locale - The locale to use for formatting (default: en-US)
 * @returns Formatted value string with trailing zeros removed
 */
export const formatAmount = (
    value: number,
    maxDecimals: number = 5,
    locale: string = 'en-US'
): string => {
    // Ensure maxDecimals is within valid range for Intl.NumberFormat (0-7)
    // Also ensure it's at least 2 for minimumFractionDigits compatibility
    const validMaxDecimals = Math.max(2, Math.min(7, Math.round(maxDecimals)));

    // Format with minimum 2 decimal places, but up to maxDecimals
    // This ensures 0 shows as "0.00" but 1.23000 shows as "1.23"
    const formatted = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: validMaxDecimals,
    }).format(value);

    // Remove trailing zeros beyond 2 decimal places
    // This handles cases like 1.230000 -> 1.23 but keeps 0.00 as "0.00"
    const parts = formatted.split('.');
    if (parts.length === 2) {
        // Remove trailing zeros but keep at least 2 digits
        const decimals = parts[1].replace(/0+$/, '');
        if (decimals.length >= 2) {
            return `${parts[0]}.${decimals}`;
        } else {
            // Pad to 2 digits if needed
            return `${parts[0]}.${decimals.padEnd(2, '0')}`;
        }
    }

    // If no decimal point, add .00 for whole numbers
    return `${formatted}.00`;
};

/**
 * Format currency amounts for account balances and monetary values (2 decimal places)
 * @param value - The value to format
 * @param currency - The currency code (optional, will use system default if not provided)
 * @param locale - The locale to use for formatting (default: en-US)
 * @returns Formatted currency string with exactly 2 fraction digits
 */
export const formatCurrencyAmount = (
    value: number,
    currency?: string,
    locale: string = 'en-US'
): string => {
    const currencyCode = currency || 'USD';

    try {
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    } catch (error) {
        const formatted = new Intl.NumberFormat(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);

        return `${formatted} ${currencyCode}`;
    }
};
